import React, { useEffect, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { Element } from 'react-scroll';
import differenceInDays from 'date-fns/difference_in_days';

import { addVoucher } from 'Redux/ducks/voucher';
import env from 'Env';
import BundleImages from 'Helpers/Product/BundleImages';
import CountdownToLaunch from 'Components/Product/Promotions/CountdownToLaunch';
import ErrorBoundary from 'Components/Utility/ErrorBoundary';
import LegalNotice from 'Components/Product/Legal/LegalNotice';
import ProductAboutSection from 'Components/Product/ProductAboutSection/ProductAboutSection';
import ProductBlogPosts from 'Components/Product/Blog/ProductBlogPosts';
import ProductBreadcrumbs from 'Components/Utility/SEO/ProductBreadcrumbs';
import ProductCarousel from 'Components/Product/Carousel/ProductCarousel';
import ProductName from 'Components/Product/ProductName';
import ProductMessages from 'Components/Product/ProductMessages/ProductMessages';
import ProductOffers from 'Components/Product/ProductOffers/ProductOffers';
import ProductRecommendations from 'Components/Product/ProductRecommendations';
import ProductRelease from 'Components/Product/ProductRelease';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ProductReviewsContext from 'Components/Product/product-reviews-context';
import ProductSEOHelmet from 'Components/Product/Seo/ProductSEOHelmet';
import ProductStructuredData from 'Components/Product/Seo/ProductStructuredData';
import RelatedProducts from 'Components/Product/RelatedProducts';
import ReviewHeaderSection from 'Components/Product/ReviewHeaderSection';
import useViewport from 'Hooks/useViewport';
import ProductCriticReviews from 'Components/Product/CriticReviews/ProductCriticReviews';
import ProductEditions from 'Components/Product/ProductEditions/ProductEditions';
import ProductHeader from 'Components/Product/ProductHeader/ProductHeader';
import MobileReviewHeader from 'Components/Product/CriticReviews/MobileReviewHeader';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import store from 'Redux/store';
import CarouselRelatedProducts from 'Components/Product/ProductRelated/CarouselRelatedProducts';
import LazyOnVisible from 'Components/Utility/LazyOnVisible/LazyOnVisible';

import 'Components/Product/ProductPage.scss';

const ProductStats = lazy(() => import(/* webpackChunkName: "ProductAboutExtras" */ 'Components/Product/ProductStats/ProductStats'));
const ProductFAQs = lazy(() => import(/* webpackChunkName: "ProductAboutExtras" */ 'Components/Product/ProductAboutSection/ProductFAQs'));
const AutoFAQs = lazy(() => import(/* webpackChunkName: "ProductAboutExtras" */ 'Components/Product/ProductAboutSection/AutoFAQs'));

const BundleCarousel = lazy(() => import(/* webpackChunkName: "BundleCarousel" */ 'Components/Bundle/BundleCarousels/BundleCarousel'));
const HowToGiftBanner = lazy(() => import(/* webpackChunkName: "HowToGiftBanner" */ 'Components/Gift/HowToGiftBanner'));
const ProductReviewsContainer = lazy(() => import(/* webpackChunkName: "ProductReviews" */ 'Components/Product/ProductReviewsContainer'));
const TotalWarRaceSection = lazy(() => import(/* webpackChunkName: "TotalWarRaceSection" */ 'Components/Product/Promotions/TotalWarRaceSection'));
const HogwartsHouses = lazy(() => import(/* webpackChunkName: "HogwartsHouses" */ 'Components/Product/Promotions/HogwartsHouses'));

const messages = defineMessages({
  related: {
    id: 'productpage.related',
    defaultMessage: 'Related',
  },
  products: {
    id: 'productpage.products',
    defaultMessage: 'products',
  },
  games: {
    id: 'productpage.games',
    defaultMessage: 'games',
  },
});

const ProductPage = ({
  product,
  intl: { formatMessage },
  showGiftBanner,
  siteBrand,
  voucher,
}) => {
  useEffect(() => {
    const discountPercent = get(product, 'current_discount.percent', 0);
    if (siteBrand === 'hpomen' && isEmpty(voucher)) {
      if (discountPercent === 0) {
        store.dispatch(addVoucher('OMEN15', getUrlParameter('ind'), true));
      } else {
        store.dispatch(addVoucher('OMENVIP', getUrlParameter('ind'), true));
      }
    }
  }, [siteBrand, voucher, product]);

  const productVideos = ProductRequirements.ProductVideos(product);
  let allBundleVideos = ProductRequirements.AllVideos(product.bundles);
  let bundleImages = BundleImages(product);
  const dates = ProductRequirements.ReleaseDates(product);
  product.preorder = ProductRequirements.isPreorder(product); // eslint-disable-line no-param-reassign, max-len

  let allGames = [];
  let totalGameCount = 0;
  const bundleTiers = cloneDeep(product.bundles);
  if (!isEmpty(bundleTiers)) {
    bundleTiers.forEach((tier) => {
      sortBy(tier.games, ['sort_order']).filter(game => game.display).forEach((game) => {
        totalGameCount += 1;
        game.totalGameIndex = totalGameCount; // eslint-disable-line no-param-reassign
        allGames = allGames.concat(game);
      });
    });
  }

  const { isAboveMd, isBelowLg, isBelowMd, isAboveSm } = useViewport();

  const allSlugs = [];
  allGames.forEach((game) => { allSlugs.push(game.slug); });

  let youtubeVideos = [];
  if (product.youtube_videos) {
    youtubeVideos = [...youtubeVideos, ...product.youtube_videos];
  }
  if (product.videos) {
    youtubeVideos = [...youtubeVideos, ...product.videos];
  }
  if (youtubeVideos.length > 8) {
    youtubeVideos = youtubeVideos.slice(0, 8);
  }

  let screenshots = product.bundles.length > 0 ? [] : product.img;

  let daysLeft = differenceInDays(new Date(dates.releaseDate), new Date());
  if (dates.earlyAccess) {
    daysLeft = differenceInDays(new Date(dates.playableFrom), new Date());
  }
  const freeToPlay = get(product, 'free_to_play', false);

  // For Multi Pack Bundles, to remove duplication, only show the game media once.
  const multiPackBundle = uniq(allSlugs).length === 1;
  if (multiPackBundle) {
    let multipackGame = {};

    if (!isEmpty(product.bundles)) {
      multipackGame = product.bundles[0].games.filter(game => game.display)[0];
    }

    screenshots = multipackGame.img;
    bundleImages = [];
    allBundleVideos = [allBundleVideos[0]];
  }

  const recommendedThreshold = env.percentRecommendedThreshold;
  const showMetaCritic = get(product, 'metaCritic.score') >= recommendedThreshold && get(product, 'metaCritic.reviews.length', 0) >= 3;
  const showOpenCritic = get(product, 'openCritic.score') >= recommendedThreshold && get(product, 'openCritic.reviews.length', 0) >= 3;

  const showCriticReviewSection = showMetaCritic || showOpenCritic;

  return (
    <main className="new-product-page">
      <ProductBreadcrumbs product={product} />
      <ProductSEOHelmet product={product} />
      <ProductReviewsContext.Consumer>
        {({ reviews }) =>
          <ProductStructuredData product={product} reviews={reviews} />
        }
      </ProductReviewsContext.Consumer>

      {showGiftBanner &&
        <Suspense>
          <div className="mt-4 container">
            <HowToGiftBanner smallBanner giftBannerHeadingTag="div" />
          </div>
        </Suspense>
      }

      <header className="product-page-header">
        <div className="product-page-header-slash" />

        {dates.prerelease && daysLeft < 30 && daysLeft >= 0 &&
          <div className="container">
            <CountdownToLaunch
              releaseDate={Date.parse(dates.releaseDate)}
              playableFrom={Date.parse(dates.playableFrom)}
              earlyAccess={dates.earlyAccess}
            />
          </div>
        }

        <section className="container">
          <div className="product-page-topper">
            <div className="name-banner-container">
              <h1 className="product-name">
                <ProductName
                  name={product.name}
                  type={product.type}
                  displayType={product.display_type}
                  drm={product.drm}
                  vrSupport={product.vr_support}
                />
              </h1>
            </div>

            <ProductRelease product={product} />

            {isAboveSm &&
              <ReviewHeaderSection product={product} />
            }

            {isBelowMd &&
              <MobileReviewHeader product={product} />
            }
          </div>
        </section>

        <ProductHeader
          product={product}
          bundleImages={bundleImages}
          screenshots={screenshots}
          videos={productVideos}
          bundleVideos={allBundleVideos}
        />
      </header>

      <div className="product-page-top-content container">
        <ProductMessages product={product} />

        {!freeToPlay &&
          <ProductOffers product={product} />
        }

        {isBelowLg &&
          <section className="page-section-margin with-dots">
            <h2 className="section-title pb-2">
              <FormattedMessage id="new-product-page.carousel.mobile.title" defaultMessage="Screenshots and videos" />
            </h2>
            <ProductCarousel
              bundleImages={bundleImages}
              screenshots={screenshots}
              videos={productVideos}
              bundleVideos={allBundleVideos}
              ratings={product.age}
              bundleCover={product.bundleCover}
              type={product.type}
              sizesOverride="(min-width: 1520px) 872px, (min-width: 1200px) 740px, (min-width: 992px) 620px, (min-width: 768px) 634px, (min-width: 576px) 445px, 95vw"
            />
          </section>
        }

        <ProductAboutSection
          product={product}
          name={product.name}
          systems={product.platforms}
          requirements={product.platform_specs}
          ztormGame={!!product.ztormId}
          catalina={product.catalina || false}
        />

        {window.locale === 'en' && !isEmpty(product.editions) &&
          <ProductEditions
            name={product.name}
            coverType={product.cover_type}
            displayType={product.display_type}
            currentPageSlug={product.slug}
            productEditions={product.editions}
            presale={product.presale}
          />
        }

        {(product.slug === 'hogwarts-legacy' || product.slug === 'hogwarts-legacy-digital-deluxe-edition') &&
          <Suspense fallback={<div />}>
            <HogwartsHouses product={product} />
          </Suspense>
        }

        {product.slug === 'total-war-warhammer-iii' &&
          <Suspense fallback={<div />}>
            <TotalWarRaceSection product={product} />
          </Suspense>
        }

        <RelatedProducts
          crossPromoteDlcSection
          product={product}
          visibleCardsNumber={5}
        />

        <CarouselRelatedProducts
          crossPromoteDlcSection
          product={product}
        />

        {window.locale === 'en' && showCriticReviewSection &&
          <Element name="critic-reviews">
            <LazyOnVisible>
              <ProductCriticReviews product={product} />
            </LazyOnVisible>
          </Element>
        }

        {!isEmpty(product.bundles) && // Lazyload because most products loaded in this template will not have bundles
          <Suspense fallback="div">
            <BundleCarousel
              allGames={allGames}
              totalGameCount={allGames.length}
              product={product}
              selectedSlide={1}
            />
          </Suspense>
        }
      </div>

      {isAboveMd &&
        <Suspense fallback="div">
          <ProductStats product={product} />
        </Suspense>
      }

      {!product.preorder && product.type !== 'bundle' && !product.mystery && !freeToPlay &&
        <ErrorBoundary hideForm>
          <Suspense>
            <ProductReviewsContainer
              product={product}
            />
          </Suspense>
        </ErrorBoundary>
      }

      <div className="product-page-bottom-content container">
        {(window.locale === 'en' && product.faqs && !!product.faqs.length) ?
          <ProductFAQs faqs={product.faqs} />
          :
          <ErrorBoundary hideForm>
            <AutoFAQs product={product} />
          </ErrorBoundary>
        }

        <RelatedProducts
          product={product}
          visibleCardsNumber={5}
        />

        {/* DLC Section */}
        <CarouselRelatedProducts
          product={product}
        />

        {(!product.sold_out || product.type !== 'bundle') &&
          <ProductRecommendations
            productPage
            title={`${formatMessage(messages.related)} ${product.display_type === 'game' ? `${formatMessage(messages.games)}` : `${formatMessage(messages.products)}`}`}
            franchises={product.franchises}
            place="product"
            cachedHits={product.giveaway}
            hitCardNameTag="h3"
          />
        }

        {window.locale === 'en' && !isEmpty(product.blogPosts) && siteBrand === 'fanatical' &&
          <ProductBlogPosts name={product.name} slug={product.slug} posts={product.blogPosts} />
        }

        <LegalNotice notice={product.notice} />
      </div>
    </main>
  );
};

ProductPage.propTypes = {
  product: PropTypes.shape({}),
  showGiftBanner: PropTypes.bool,
  siteBrand: PropTypes.string,
  voucher: PropTypes.shape({}),
};

ProductPage.defaultProps = {
  product: {},
  showGiftBanner: false,
  siteBrand: 'fanatical',
  voucher: {},
  smartRecommendedNotInCart: [],
};

export default injectIntl(ProductPage);
